/**
 * Audit-Trail Vorher/Nachher-Diff (Spec 530, Phase 4, Zone C).
 *
 * Zweispaltige, farbkodierte Gegenueberstellung von `previousValue` (entfernt-Ton)
 * und `newValue` (hinzugefuegt-Ton). Redaktions-Marker erscheinen mit Schloss-Icon
 * und `changeKind` statt eines Wertes (nie das Geheimnis). `changedFields` und ein
 * `deltaTruncated`-Hinweis werden mitgefuehrt.
 */

import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import type { AuditEvent, AuditFieldValue } from '@/types/audit';

function isRedacted(v: unknown): v is { redacted: true; changeKind?: string } {
  return typeof v === 'object' && v !== null && (v as { redacted?: boolean }).redacted === true;
}

function DiffValue({ value }: { value: AuditFieldValue | undefined }) {
  const { t } = useTranslation('audit');
  if (value === undefined) return <span className="text-muted-foreground">—</span>;
  if (isRedacted(value)) {
    // changeKind ist ein Backend-Enum (rotated/set/cleared) → uebersetzen, mit
    // Fallback auf den Rohwert, falls kuenftig ein unbekannter Wert kommt.
    const kindLabel = value.changeKind
      ? t(`detail.changeKind.${value.changeKind}`, { defaultValue: value.changeKind })
      : '';
    return (
      <span className="inline-flex items-center gap-1 text-sm" data-testid="audit-redacted">
        <Lock className="h-3 w-3" aria-hidden="true" />
        {t('detail.redacted')}
        {kindLabel ? ` (${kindLabel})` : ''}
      </span>
    );
  }
  if (value === null) return <span className="font-mono text-xs">null</span>;
  if (typeof value === 'object') {
    return <pre className="whitespace-pre-wrap break-all font-mono text-xs">{JSON.stringify(value, null, 2)}</pre>;
  }
  return <span className="break-all font-mono text-xs">{String(value)}</span>;
}

function DiffColumn({
  label,
  value,
  tone,
  testid,
}: {
  label: string;
  value: AuditFieldValue | undefined;
  tone: 'prev' | 'next';
  testid: string;
}) {
  const toneCls =
    tone === 'prev' ? 'border-destructive/30 bg-destructive/5' : 'border-success/30 bg-success/5';
  return (
    <div className={`rounded-md border p-2 ${toneCls}`} data-testid={testid}>
      <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
      <DiffValue value={value} />
    </div>
  );
}

export function AuditDiffDisplay({ event }: { event: AuditEvent }) {
  const { t } = useTranslation('audit');
  const hasPrev = event.previousValue !== undefined;
  const hasNext = event.newValue !== undefined;
  const hasChangedFields = Boolean(event.changedFields && event.changedFields.length > 0);

  // Auch ein reines `deltaTruncated`-Event (ohne prev/next/changedFields) muss den
  // Hinweis zeigen — der darf nie verschwinden.
  if (!hasPrev && !hasNext && !hasChangedFields && !event.deltaTruncated) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="audit-diff">
      {hasChangedFields && (
        <div className="text-xs">
          <span className="font-medium text-muted-foreground">{t('detail.changedFields')}: </span>
          {event.changedFields!.join(', ')}
        </div>
      )}
      {(hasPrev || hasNext) && (
        <div className="grid gap-2 md:grid-cols-2">
          <DiffColumn
            testid="audit-diff-prev"
            label={t('detail.previous')}
            value={event.previousValue}
            tone="prev"
          />
          <DiffColumn testid="audit-diff-next" label={t('detail.new')} value={event.newValue} tone="next" />
        </div>
      )}
      {event.deltaTruncated && (
        <p className="text-xs text-muted-foreground" data-testid="audit-delta-truncated">
          {t('detail.deltaTruncated')}
        </p>
      )}
    </div>
  );
}
